import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { track } from "./analytics";
import { App } from "./components/App";
import { OnboardingSurveyProvider } from "./components/onboarding/context";
import { TooltipProvider } from "./components/ui/tooltip";
import { AnnouncerProvider } from "./hooks/use-announcer";
import { AppearanceProvider } from "./hooks/use-appearance";

import "./assets/tailwind.css";

function reportReactError(kind: "uncaught" | "caught" | "recoverable") {
  return (error: unknown, errorInfo?: { componentStack?: string }) => {
    console.error(`[react:${kind}]`, error);
    try {
      track("react_error", {
        kind,
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack?.slice(0, 2000) : undefined,
        component_stack: errorInfo?.componentStack?.slice(0, 2000),
      });
    } catch {}
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const container = document.getElementById("app");

if (container) {
  const root = createRoot(container, {
    onUncaughtError: reportReactError("uncaught"),
    onCaughtError: reportReactError("caught"),
    onRecoverableError: reportReactError("recoverable"),
  });
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AppearanceProvider>
          {/* Outside <App /> so the router never unmounts the live regions. */}
          <AnnouncerProvider>
            <TooltipProvider>
              {/* Inside AnnouncerProvider (the survey announces) and QueryClientProvider
                  (it reads /api/myself/); wraps <App /> so the account menu can open it. */}
              <OnboardingSurveyProvider>
                <App />
              </OnboardingSurveyProvider>
            </TooltipProvider>
          </AnnouncerProvider>
        </AppearanceProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}
